import * as React from "react";

import { assertNever } from "@impero-com/common/utils/obj";

import {
    RadioInput,
    RadioInputOption,
    RadioInputProps,
} from "components/form/legacyFields/radioInput";
import * as CT from "org/risks/content/types";
import * as R from "org/risks/types";

import { mapForm, simpleMappers, WrappedFormUtils } from "../../../utils/form";
import * as U from "../content/utils";

import * as theme from "./theme.css";
import * as T from "./types";

type RadioMetadataFieldProps<ROOT> = T.SharedProperties<
    string,
    ROOT,
    CT.RadioButtonGroupInstance
> & { columns: number };

export default class RadioMetadataField<ROOT> extends React.PureComponent<
    RadioMetadataFieldProps<ROOT>
> {
    render = () => {
        const { id, label, instance, columns } = this.props;
        const { fieldType } = instance;
        const baseProps = {
            id,
            className: theme.radioMetadataField,
            label,
            options: fieldType.options.map(mapOptionValueToRadioInputOption),
            columns,
        };
        let props: RadioInputProps<string, ROOT>;
        switch (this.props.type) {
            case "CREATE":
                props = {
                    ...baseProps,
                    type: this.props.type,
                    initialValue: undefined,
                    form: mapInstanceFormToOptionIdForm(
                        id,
                        this.props.form,
                        instance
                    ),
                };
                break;
            case "EDIT":
                props = {
                    ...baseProps,
                    type: this.props.type,
                    value: this.props.instance.value?.id,
                    onChange: this.handleOnChange,
                    validationError: this.props.validationError,
                };
                break;
            case "READ_ONLY":
                props = {
                    ...baseProps,
                    type: this.props.type,
                    value: this.props.instance.value?.id,
                };
                break;
            default:
                return assertNever(this.props);
        }
        return <RadioInput {...props} />;
    };

    private handleOnChange = (value: string | undefined) => {
        const { instance } = this.props;
        if (this.props.type === "EDIT") {
            return this.props.onChangeAsync(
                Promise.resolve(value),
                (_prev, val) =>
                    U.extractMetadataInstanceValue(
                        optionIdToInstance(val, instance)
                    )
            );
        }
        return Promise.reject(
            new Error("Cannot change value in non-edit mode")
        );
    };
}

function instanceToOptionId(instance: CT.MetadataInstance): string | undefined {
    if (instance.type !== "RadioButtonGroupInstance") {
        throw new Error("Expected radio instance, got " + instance.type);
    }
    const { value } = instance;
    return value?.id;
}

function optionIdToInstance(
    optionId: string | undefined,
    instance: CT.MetadataInstance
): CT.MetadataInstance {
    if (instance.type !== "RadioButtonGroupInstance") {
        throw new Error("Expected radio instance, got " + instance.type);
    }
    const { options } = instance.fieldType;
    let newValue: R.OptionValue | undefined;
    if (optionId !== undefined) {
        newValue = options.find((opt) => opt.id === optionId);
        if (newValue === undefined) {
            throw new Error("Unknown option id " + optionId);
        }
    } else {
        newValue = undefined;
    }

    return {
        ...instance,
        value: newValue,
    };
}

function mapInstanceFormToOptionIdForm<ROOT>(
    id: string,
    instanceForm: WrappedFormUtils<
        {
            [x: string]: CT.MetadataInstance;
        },
        ROOT
    >,
    instance: CT.MetadataInstance
): WrappedFormUtils<
    {
        [x: string]: string | undefined;
    },
    ROOT
> {
    const mapper = simpleMappers<
        string,
        CT.MetadataInstance,
        string | undefined,
        ROOT
    >(id, instanceToOptionId, (optionId) =>
        optionIdToInstance(optionId, instance)
    );
    return mapForm<Record<string, string | undefined>>()(
        instanceForm,
        mapper()
    );
}

function mapOptionValueToRadioInputOption(
    optionValue: R.OptionValue
): RadioInputOption {
    return {
        value: optionValue.id,
        label: optionValue.value,
    };
}
